import { Segment, CalibrationProfile, AidStation } from '../types'
import { calculateETAAtDistance } from './timeline'

// Default calibration coefficients
const DEFAULT_COEFFICIENTS = {
  beta0_base_pace_min_per_km: 6.42,
  beta1_uphill_min_per_m_per_km: 0.035,
  beta2_downhill_min_per_m_per_km: -0.012,
  beta3_fatigue_min_per_km: 0.000,
  use_fatigue: false
}

export function calculateSegmentPace(
  segment: Segment,
  calibrationProfile?: CalibrationProfile | {
    beta0_base_pace_min_per_km: number;
    beta1_uphill_min_per_m_per_km: number;
    beta2_downhill_min_per_m_per_km: number;
    beta3_fatigue_min_per_km: number;
    use_fatigue: boolean;
  },
  cumulativeDistance = 0
): number {
  const profile = calibrationProfile || DEFAULT_COEFFICIENTS
  
  const gainRate = (segment.elev_gain_m || 0) / segment.distance_km
  const lossRate = Math.min((segment.elev_loss_m || 0) / segment.distance_km, 80) // Cap at 80 m/km
  const fatigue = profile.use_fatigue ? profile.beta3_fatigue_min_per_km * cumulativeDistance : 0
  
  return profile.beta0_base_pace_min_per_km
       + profile.beta1_uphill_min_per_m_per_km * gainRate
       + profile.beta2_downhill_min_per_m_per_km * lossRate
       + fatigue
}

export function formatTime(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  
  if (hours > 0) {
    return `${hours}h ${mins}m`
  }
  return `${mins}m`
}

export function formatPace(minutes: number): string {
  const mins = Math.floor(minutes)
  const secs = Math.round((minutes % 1) * 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export function formatPaceTime(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const mins = Math.floor(minutes % 60)
  const secs = Math.round((minutes % 1) * 60)
  
  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export function calculateTotalDistance(segments: Segment[]): number {
  return segments.reduce((total, segment) => total + segment.distance_km, 0)
}

export function calculateTotalTime(segments: Segment[]): number {
  return segments.reduce((total, segment) => {
    const pace = calculateSegmentPace(segment)
    return total + (segment.distance_km * pace) + (segment.planned_stop_min || 0)
  }, 0)
}

export function validateAidStationDistance(
  proposedDistance: number,
  segments: Segment[],
  existingAidStations: AidStation[] = [],
  excludeId?: string
): { isValid: boolean; message?: string } {
  const totalDistance = calculateTotalDistance(segments)
  
  if (proposedDistance < 0) {
    return { isValid: false, message: 'Distance cannot be negative' }
  }
  
  if (proposedDistance > totalDistance) {
    return { 
      isValid: false, 
      message: `Distance cannot exceed course total (${totalDistance.toFixed(2)} km)` 
    }
  }
  
  // Check for duplicate distances (within 0.01 km)
  const others = existingAidStations.filter(as => as.id !== excludeId)
  const tooClose = others.find(as => Math.abs(as.at_distance_km - proposedDistance) < 0.01)
  
  if (tooClose) {
    return { 
      isValid: false, 
      message: 'Another aid station already exists at this distance' 
    }
  }
  
  return { isValid: true }
}

export function calculateArrivalTime(
  distanceKm: number,
  segments: Segment[],
  calibrationProfile?: CalibrationProfile | {
    beta0_base_pace_min_per_km: number;
    beta1_uphill_min_per_m_per_km: number;
    beta2_downhill_min_per_m_per_km: number;
    beta3_fatigue_min_per_km: number;
    use_fatigue: boolean;
  },
  aidStations?: AidStation[]
): number {
  // Use the centralized timeline calculation for consistency
  // This ensures aid station stops are properly accounted for
  return calculateETAAtDistance(distanceKm, segments, aidStations || [], calibrationProfile)
}